import { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import api from "@/lib/api";

export default function RbacAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/rbac/audit").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Failed to load audit.</div>;

  const allOk = data.invariants.every((i) => i.ok);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="rbac-audit-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={22} className="text-orange-600" /> RBAC Audit</h1>
          <p className="text-sm text-slate-500 mt-0.5">Verify role-based access control invariants across the system.</p>
        </div>
        <button onClick={load} className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-sm rounded-md flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className={`border rounded-lg p-5 mb-6 ${allOk ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
        <div className="flex items-center gap-2">
          {allOk ? <CheckCircle2 size={22} className="text-emerald-600" /> : <XCircle size={22} className="text-red-600" />}
          <div className="font-semibold">
            {allOk ? "All RBAC invariants pass" : "Violations detected"}
          </div>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Generated at {new Date(data.generated_at).toLocaleString("en-IN")}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="font-semibold text-slate-900 mb-4">Invariants</h3>
        <div className="space-y-2.5">
          {data.invariants.map((inv, i) => (
            <div key={i} className={`flex items-start gap-3 p-3 rounded-md border ${inv.ok ? "bg-emerald-50/50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              {inv.ok ? <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{inv.check}</div>
                <div className="text-xs text-slate-600 mt-0.5">{inv.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatBlock title="User Role Distribution">
          {Object.entries(data.user_role_distribution).map(([role, count]) => (
            <div key={role} className="flex justify-between py-1 text-sm">
              <span className="text-slate-600 capitalize">{role.replace("_", " ")}</span>
              <span className="font-mono font-semibold">{count}</span>
            </div>
          ))}
        </StatBlock>
        <StatBlock title="NPDs by Owner Role">
          {Object.entries(data.npds_by_owner_role).map(([role, count]) => (
            <div key={role} className="flex justify-between py-1 text-sm">
              <span className="text-slate-600 capitalize">{role.replace("_", " ")}</span>
              <span className="font-mono font-semibold">{count}</span>
            </div>
          ))}
        </StatBlock>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold text-slate-900 mb-3">Data Counts</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(data.counts).map(([k, v]) => (
            <div key={k} className="bg-slate-50 rounded-md p-3 text-center">
              <div className="text-2xl font-bold font-mono text-slate-900">{v}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">{k}</div>
            </div>
          ))}
        </div>
      </div>

      {data.orphaned_npds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-5 mt-4">
          <h3 className="font-semibold text-red-700 mb-2">Orphaned NPDs ({data.orphaned_npds.length})</h3>
          <ul className="text-sm text-red-800 space-y-1">
            {data.orphaned_npds.map((o, i) => (
              <li key={i} className="font-mono">{o.npd_number}: assigned_to={o.assigned_to}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatBlock({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h3 className="font-semibold text-slate-900 mb-3 text-sm">{title}</h3>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}
